import {
  FailedGroup,
  FailedMediaListModal,
  FailedRow,
} from '@components/FailedMediaList/FailedMediaListModal';
import { NotifBellEntry, NotifBellEntryDetails, notificationStore } from '@stores/Notifications';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

/** Handlers passed down to the notifications panel body + its cards. */
export interface NotifPanelHandlers {
  onDismiss: (entry: NotifBellEntry) => void;
  onOpenDetails: (details: NotifBellEntryDetails, variant: 'success' | 'error') => void;
}

/**
 * Owns the notifications-surface controller state (the "More info"
 * FailedMediaListModal) and shared handlers, and renders that modal. Returned
 * separately from the handlers so the caller mounts it at a PERSISTENT location
 * (outside the collapsible popover / drawer). No close-on-open hook is needed:
 * on mobile the details view is a drawer (FailedMediaListModal) that stacks
 * ABOVE the notifications drawer, so the underlying drawer stays open.
 */
export function useNotifActivity(): {
  handlers: NotifPanelHandlers;
  modals: ReactNode;
} {
  // Set when the user clicks a card's "More info"; cleared on modal close.
  const [detailsContext, setDetailsContext] = useState<{
    details: NotifBellEntryDetails;
    variant: 'success' | 'error';
  } | null>(null);

  const handleDismiss = useCallback((entry: NotifBellEntry) => {
    if (entry.toastId != null) toast.dismiss(entry.toastId);
    notificationStore.remove(entry.id);
  }, []);

  const handleOpenDetails = useCallback(
    (details: NotifBellEntryDetails, variant: 'success' | 'error') => {
      setDetailsContext({ details, variant });
    },
    [],
  );

  // Reshape the details payload into FailedMediaListModal's prop shape —
  // one synthetic group, one row per filename, no per-row actions.
  const detailsGroups = useMemo<FailedGroup[]>(() => {
    if (!detailsContext || detailsContext.details.kind !== 'media-list') return [];
    const rows: FailedRow[] = detailsContext.details.filenames.map((name, i) => ({
      key: `${i}:${name}`,
      filename: name,
      isAudio: false,
      thumbnailUrl: null,
    }));
    return [{ key: 'details', rows }];
  }, [detailsContext]);

  const handlers: NotifPanelHandlers = {
    onDismiss: handleDismiss,
    onOpenDetails: handleOpenDetails,
  };

  const modals = (
    <FailedMediaListModal
      open={detailsContext !== null}
      onClose={() => setDetailsContext(null)}
      title={detailsContext?.details.title ?? ''}
      metaText={
        detailsContext?.details.kind === 'media-list'
          ? `${detailsContext.details.filenames.length} ${
              detailsContext.details.filenames.length === 1 ? 'item' : 'items'
            }`
          : ''
      }
      groups={detailsGroups}
      variant={detailsContext?.variant ?? 'error'}
      // Surface the entry's retry hook as the modal's primary footer action.
      // Closing the modal after dispatch is intentional: the retry's outcome
      // lands as a new toast (and a new bell card if it fails again), so
      // leaving the modal open would just stack stale context behind it.
      onRetryAll={
        detailsContext?.details.onRetryAll
          ? async () => {
              const cb = detailsContext.details.onRetryAll!;
              setDetailsContext(null);
              await cb();
            }
          : undefined
      }
      retryAllLabel={detailsContext?.details.retryAllLabel}
    />
  );

  return { handlers, modals };
}
